import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { InputField } from "../../../components/forms/InputField";
import { QuestionWithRelations } from "../../../../../api/src/types";
import { validateTextWithRestriction } from "../utils/questionnaire";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import { QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT } from "../../../components/theme";
import { InputWrapper, ErrorWrapper } from "./SharedFormStyles";

interface TextFieldInputProps {
  question: QuestionWithRelations;
}

const TextFieldInput: React.FC<TextFieldInputProps> = ({ question }) => {
  const [textInput, setTextInput] = useState<string | null>(
    question.answers[0]?.valueTyped ?? null,
  );
  const [textError, setTextError] = useState<string | null>(null);
  const { data: user } = useGetCurrentUserQuery();

  const [
    createAnswer,
    {
      /*isLoading: isCreating,*/
      isError: isCreateError,
      /*isSuccess: isCreateSuccess,*/
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      /*isLoading: isUpdating,*/
      isError: isUpdateError,
      /*isSuccess: isUpdateSuccess,*/
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  const hasAnswer = question.answers.length > 0;

  const save = useDebouncedCallback((value: string | null) => {
    if (!user) return;

    if (!hasAnswer && !value) return;

    const payload = {
      questionId: question.id,
      userId: user.id,
      valueTyped: value,
    };

    if (!hasAnswer) void createAnswer(payload);
    else void updateAnswer(payload);
  }, 1000);

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    const value = e.target.value;
    setTextInput(value);

    // Validate text input with restrictions
    const error = validateTextWithRestriction(value, question);
    setTextError(error);

    if (error) {
      save(null);
      return;
    }
    save(value);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmedValue = e.target.value.trim();

    // Update local state for immediate visual feedback; backend will handle trimming on save
    if (trimmedValue !== textInput) {
      setTextInput(trimmedValue);
    }
  };

  //const isLoading = isCreating || isUpdating;
  const hasError = isCreateError || isUpdateError;
  /*const isSuccess =
    (isCreateSuccess || isUpdateSuccess) &&
    !hasError &&
    !isLoading &&
    !textError &&
    textInput !== "";
  */
  return (
    <>
      <InputWrapper>
        <InputField
          type="text"
          value={textInput ?? ""}
          onChange={(e) => {
            handleTextInputChange(e);
          }}
          onBlur={handleBlur}
          placeholder={QUESTIONNAIRE_INPUT_PLACEHOLDER_TEXT}
          required={question.isRequired}
          errorMessage={textError}
        />
        {/* Commenting out the loader and success icon for now as per the request of the design team */}
        {/*
        {isLoading && (
          <IconContainer>
            <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
          </IconContainer>
        )}
        {isSuccess && (
          <IconContainer>
            <GreenTick size={isMobile ? IconSize.S : IconSize.XL} />
          </IconContainer>
        )}
        */}
      </InputWrapper>
      {hasError && (
        <ErrorWrapper>
          Error saving answer, please refresh the page and try again
        </ErrorWrapper>
      )}
    </>
  );
};

export default TextFieldInput;
